import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  CameraIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';

const DOCUMENT_TYPES = [
  {
    id: 'passport',
    name: 'パスポート',
    description: '所持人記入欄があるページ',
    icon: '',
    requirements: ['顔写真が鮮明に写っている', '文字が読める', '有効期限内である'],
    needsBackSide: false
  },
  {
    id: 'drivers_license',
    name: '運転免許証',
    description: '表面・裏面両方を撮影してください',
    icon: '',
    requirements: ['顔写真が鮮明に写っている', '文字が読める', '有効期限内である', '表面・裏面両方の撮影が必要'],
    needsBackSide: true
  },
  {
    id: 'mynumber_card',
    name: 'マイナンバーカード',
    description: '表面（顔写真がある面）',
    icon: '',
    requirements: ['顔写真が鮮明に写っている', '文字が読める', '有効期限内である'],
    needsBackSide: false
  },
  {
    id: 'residence_card',
    name: '住民基本台帳カード',
    description: '顔写真付きのもの',
    icon: '',
    requirements: ['顔写真が鮮明に写っている', '文字が読める', '有効期限内である'],
    needsBackSide: false
  }
];

const IdentityVerificationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [currentStep, setCurrentStep] = useState('select'); // select, capture, capture_back, review, submit
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  // 表示用プレビューURL
  const [capturedImagePreview, setCapturedImagePreview] = useState(null);
  const [capturedBackImagePreview, setCapturedBackImagePreview] = useState(null);
  // 送信用の File オブジェクト
  const [capturedImageFile, setCapturedImageFile] = useState(null);
  const [capturedBackImageFile, setCapturedBackImageFile] = useState(null);
  const [capturingSide, setCapturingSide] = useState('front'); // 'front' or 'back'
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('initial'); // initial, pending, approved, rejected, none

  React.useEffect(() => {
    const checkStatus = async () => {
      if (user?.is_identity_verified) {
        setStatus('approved');
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const authApi = apiWithAuth(token);
        const res = await authApi.get('/identity-verification/status');
        if (res.data && res.data.status) {
          setStatus(res.data.status);
        } else {
          setStatus('none');
        }
      } catch {
        setStatus('none');
      }
    };
    checkStatus();
  }, [user]);

  if (status === 'approved') {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow text-center max-w-sm w-full">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">本人確認済みです</h2>
          <p className="text-gray-600 mb-6">あなたの本人確認は完了しています。</p>
          <button onClick={() => navigate('/mypage')} className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold">マイページへ戻る</button>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⏳</span>
          </div>
          <h2 className="text-xl font-bold mb-2">審査中です</h2>
          <p className="text-gray-600 mb-6">現在、提出された本人確認書類を確認中です。<br />審査完了まで1-2営業日お待ちください。</p>
          <button onClick={() => navigate('/mypage')} className="w-full bg-gray-600 text-white py-2 rounded-lg font-bold">マイページへ戻る</button>
        </div>
      </div>
    );
  }

  const handleDocumentSelect = (documentType) => {
    setSelectedDocumentType(documentType);
    setCurrentStep('capture');
  };

  const handleImageCapture = (event) => {
    const file = event.target.files[0];
    if (file) {
      // ファイルサイズチェック (5MB制限)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('ファイルサイズは5MB以下にしてください');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      if (capturingSide === 'front') {
        setCapturedImageFile(file);
        setCapturedImagePreview(previewUrl);
        // 運転免許証の場合は裏面も撮影
        if (selectedDocumentType?.needsBackSide) {
          setCapturingSide('back');
          setCurrentStep('capture_back');
        } else {
          setCurrentStep('review');
        }
      } else {
        setCapturedBackImageFile(file);
        setCapturedBackImagePreview(previewUrl);
        setCurrentStep('review');
      }
    }
  };

  const handleRetakePhoto = () => {
    if (capturingSide === 'back') {
      setCapturedBackImagePreview(null);
      setCapturedBackImageFile(null);
      setCurrentStep('capture_back');
    } else {
      setCapturedImagePreview(null);
      setCapturedBackImagePreview(null);
      setCapturedImageFile(null);
      setCapturedBackImageFile(null);
      setCapturingSide('front');
      setCurrentStep('capture');
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!capturedImageFile || !selectedDocumentType) {
      toast.error('必要な情報が不足しています');
      return;
    }

    // 運転免許証の場合は裏面も必須
    if (selectedDocumentType.needsBackSide && !capturedBackImageFile) {
      toast.error('運転免許証の裏面も撮影してください');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);

      const submitData = new FormData();
      submitData.append('document_type', selectedDocumentType.id);
      submitData.append('document_front_image', capturedImageFile, capturedImageFile.name || 'front.jpg');

      // 裏面画像がある場合は追加
      if (capturedBackImageFile) {
        submitData.append('document_back_image', capturedBackImageFile, capturedBackImageFile.name || 'back.jpg');
      }

      await authApi.post('/identity-verification', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      // 提出後の最新ステータスを取得して、モードに応じたメッセージを表示
      try {
        const statusRes = await authApi.get('/identity-verification/status');
        const st = (statusRes?.data?.status || '').toLowerCase();
        if (st === 'completed' || st === 'approved') {
          toast.success('本人確認が自動承認されました');
        } else {
          toast.success('本人確認書類を提出しました。審査完了まで1-2営業日お待ちください。');
        }
      } catch (e) {
        // 失敗しても従来メッセージ
        toast.success('本人確認書類を提出しました。審査完了まで1-2営業日お待ちください。');
      }
      navigate('/mypage');
    } catch (error) {
      console.error('Identity verification submission failed:', error);
      if (error.response?.status === 400) {
        toast.error('既に本人確認が提出済みです');
      } else {
        toast.error('提出に失敗しました。もう一度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (currentStep === 'review') {
      if (selectedDocumentType?.needsBackSide && capturedBackImagePreview) {
        setCurrentStep('capture_back');
        setCapturingSide('back');
      } else {
        setCurrentStep('capture');
        setCapturingSide('front');
      }
    } else if (currentStep === 'capture_back') {
      setCurrentStep('capture');
      setCapturingSide('front');
    } else if (currentStep === 'capture') {
      setCurrentStep('select');
      setSelectedDocumentType(null);
      setCapturedImagePreview(null);
      setCapturedBackImagePreview(null);
      setCapturedImageFile(null);
      setCapturedBackImageFile(null);
      setCapturingSide('front');
    } else {
      navigate('/mypage');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <button
            onClick={goBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">本人確認</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6">
        {/* Step 1: Document Type Selection */}
        {currentStep === 'select' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <IdentificationIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">本人確認書類を選択</h2>
              <p className="text-gray-600">
                以下の書類から1つを選択して撮影してください
              </p>
            </div>

            <div className="space-y-4">
              {DOCUMENT_TYPES.map((docType) => (
                <button
                  key={docType.id}
                  onClick={() => handleDocumentSelect(docType)}
                  className="w-full bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{docType.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900">{docType.name}</h3>
                        <p className="text-sm text-gray-600">{docType.description}</p>
                      </div>
                    </div>
                    <ChevronLeftIcon className="h-5 w-5 text-gray-400 transform rotate-180" />
                  </div>
                </button>
              ))}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">ご注意</h4>
                  <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                    <li> 書類全体が写るように撮影してください</li>
                    <li> 文字が鮮明に読めることを確認してください</li>
                    <li> 有効期限内の書類をご使用ください</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 不正提出に関する重要な警告 */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-red-800 mb-1">重要なお知らせ（不正提出の禁止）</h4>
              <p className="text-sm text-red-800 leading-6">
                偽造・改ざんを含む不正な本人確認書類の提出は固く禁止されています。違反が判明した場合、
                本サービスの<strong>永久的な利用禁止</strong>、アカウントの<strong>停止</strong>、および必要に応じて
                <strong>法的措置</strong>を講じます。正確で真正な書類のみを提出してください。
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Image Capture (Front) */}
        {currentStep === 'capture' && selectedDocumentType && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CameraIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {selectedDocumentType.name}（表面）を撮影
              </h2>
              <p className="text-gray-600">
                {selectedDocumentType.description}
              </p>
            </div>

            <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
              <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">表面の写真を撮影またはファイルを選択</p>
              <p className="text-sm text-gray-500 mb-4">ボタンを押すとカメラが起動し、撮影後に確認画面で最終提出できます</p>

              {/* カメラ起動用 */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageCapture}
                className="hidden"
              />
              {/* フォトライブラリ用 */}
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageCapture}
                className="hidden"
              />

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  <CameraIcon className="w-5 h-5 inline mr-2" />
                  表面を撮影
                </button>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  className="bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors border"
                >
                  写真から選ぶ
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">撮影のポイント</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                {selectedDocumentType.requirements.map((req, index) => (
                  <li key={index}> {req}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Step 2.5: Image Capture (Back) - For driver's license only */}
        {currentStep === 'capture_back' && selectedDocumentType && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CameraIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {selectedDocumentType.name}（裏面）を撮影
              </h2>
              <p className="text-gray-600">
                続いて裏面を撮影してください
              </p>
            </div>

            <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
              <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">裏面の写真を撮影またはファイルを選択</p>
              <p className="text-sm text-gray-500 mb-4">裏面の撮影が完了すると確認画面に進みます</p>

              {/* カメラ起動用 */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageCapture}
                className="hidden"
              />
              {/* フォトライブラリ用 */}
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageCapture}
                className="hidden"
              />

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  <CameraIcon className="w-5 h-5 inline mr-2" />
                  裏面を撮影
                </button>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  className="bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors border"
                >
                  写真から選ぶ
                </button>
              </div>
            </div>

            {capturedImagePreview && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-sm font-medium text-green-800">表面の撮影が完了しました</span>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">撮影のポイント</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li> 免許証の裏面全体が写るように撮影してください</li>
                <li> 文字が鮮明に読めることを確認してください</li>
                <li> 反射やぼけがないことを確認してください</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === 'review' && capturedImagePreview && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-2">撮影内容の確認</h2>
              <p className="text-gray-600">
                画像に問題がないか確認してください
              </p>
            </div>

            {/* 表面画像 */}
            <div className="bg-white rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">表面</h3>
              <img
                src={capturedImagePreview}
                alt="撮影した本人確認書類（表面）"
                className="w-full rounded-lg border border-gray-200"
              />
            </div>

            {/* 裏面画像（運転免許証の場合） */}
            {capturedBackImagePreview && (
              <div className="bg-white rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">裏面</h3>
                <img
                  src={capturedBackImagePreview}
                  alt="撮影した本人確認書類（裏面）"
                  className="w-full rounded-lg border border-gray-200"
                />
              </div>
            )}

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">確認項目</h4>
                  <ul className="text-sm text-green-700 mt-1 space-y-1">
                    <li>書類全体が写っている</li>
                    <li>文字が鮮明に読める</li>
                    <li>反射やぼけがない</li>
                    {capturedBackImagePreview && <li>表面・裏面両方撮影完了</li>}
                  </ul>
                </div>
              </div>
            </div>

            {/* 提出前の警告 */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-red-800 mb-1">提出にあたっての重要な注意</h4>
              <p className="text-sm text-red-800 leading-6">
                偽造・改ざんされた本人確認書類の提出が確認された場合、<strong>アカウントの永久停止</strong>や
                <strong>法的措置</strong>の対象となります。内容に誤りがないか、今一度ご確認ください。
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleRetakePhoto}
                className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                撮り直し
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '送信中...' : 'この内容で提出する'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdentityVerificationPage;